import { makeAutoObservable, runInAction } from "mobx";
import {
  IsString,
  IsNotEmpty,
  MinLength,
  Matches,
  validate,
  ValidationError,
} from "class-validator";
import {
  superadminCreateServiceCenter,
  superadminUpdateServiceCenter,
  superadminCreateServiceCenterAdmin,
  superadminListServiceCenterAdmins,
  dadataControllerSuggestAddress,
} from "@pro-sto/api-client";
import { ApiError } from "@pro-sto/api-client";
import type { SuperadminServiceCenterDetailDto } from "@pro-sto/api-client";
import { toastStore } from "./ToastStore";

// ─── Admin form item ───

export interface AdminFormItem {
  id: string;
  /** Local phone digits only (no + and no CC). For isExisting admins — full phone string from API. */
  phone: string;
  /** Country code digits (e.g. "7", "44", "375"). Only used for new admins. */
  phoneRegion?: string;
  /** Existing admin from server — skip creation */
  isExisting?: boolean;
}

// ─── DTO validation ───

export class ServiceCenterFormDto {
  @IsString()
  @IsNotEmpty({ message: "Название обязательно" })
  @MinLength(2, { message: "Минимум 2 символа" })
  name: string = "";

  @IsString()
  @IsNotEmpty({ message: "Адрес обязательно" })
  address: string = "";
}

export class AdminFormDto {
  @IsString()
  @IsNotEmpty({ message: "Телефон обязательно" })
  @Matches(/^\+\d{8,14}$/, { message: "Введите корректный номер телефона" })
  phone: string = "";
}

// ─── Address suggestion ───

export interface AddressSuggestion {
  label: string;
  lat: number;
  lon: number;
}

// ─── Internal Dadata response shapes ───

interface DadataSuggestionRaw {
  value: string;
  data: {
    geo_lat: string | null;
    geo_lon: string | null;
  };
}

interface DadataAddressResponse {
  suggestions: DadataSuggestionRaw[];
}

interface ServiceCenterAdminRaw {
  uuid: string;
  phone: string;
}

interface CreateServiceCenterResult {
  uuid: string;
}

// ─── Helper: extract Russian error message from API response ───

function extractApiError(err: unknown, fallback: string): string {
  if (err instanceof ApiError) {
    const body = err.body as {
      message?: string | string[];
      error?: string;
    } | null;
    const msg = body?.message;
    if (typeof msg === "string" && msg.length > 0) return localizeMsg(msg);
    if (Array.isArray(msg) && msg.length > 0) return localizeMsg(msg[0]);
    if (body?.error) return localizeMsg(body.error);
    if (err.status === 409)
      return "Пользователь с таким номером уже существует.";
    if (err.status === 422)
      return "Некорректные данные. Проверьте номер телефона.";
    if (err.status === 400) return "Некорректные данные запроса.";
    if (err.status === 500) return "Ошибка сервера. Попробуйте позже.";
  }
  return fallback;
}

const MSG_MAP: Record<string, string> = {
  "already exists": "Пользователь с такими данными уже существует.",
  "email.*taken": "Админ с таким email уже зарегистрирован.",
  "phone.*taken": "Номер телефона уже занят.",
  "invalid.*phone": "Некорректный формат номера телефона.",
  password: "Недостаточный пароль админа.",
};

function localizeMsg(msg: string): string {
  const lower = msg.toLowerCase();
  for (const [pattern, ru] of Object.entries(MSG_MAP)) {
    if (new RegExp(pattern, "i").test(lower)) return ru;
  }
  return msg;
}

// ─── Helper: build admin request body from phone ───

function buildAdminRequestBody(phone: string) {
  const digits = phone.replace(/\D/g, "");
  const ts = Date.now();
  return {
    name: "Администратор",
    phone,
    email: `admin-${digits}-${ts}@prosto-app.ru`,
    password: "Prosto" + digits.slice(-4) + "!2",
  };
}

// ─── Store ───

class ServiceCenterFormStore {
  isOpen = false;
  isEditing = false;
  editUuid: string | null = null;

  // Form data
  name = "";
  address = "";
  lat = 55.7558;
  lon = 37.6173;
  isPublished = false;
  admins: AdminFormItem[] = [];

  // Address suggestions
  addressQuery = "";
  suggestions: AddressSuggestion[] = [];
  showSuggestions = false;
  isSuggestLoading = false;

  // Geo banners
  /** Set when marker is dragged: reverse-geocoded address to confirm */
  pendingReverseAddress: string | null = null;
  /** Set when address typed+blurred: first suggestion to apply to map */
  pendingSuggestedCoords: AddressSuggestion | null = null;

  // Validation
  validationErrors: Record<string, string> = {};
  adminErrors: Record<string, Record<string, string>> = {};
  adminSubmitErrors: Record<string, string> = {};
  submitAttempted = false;

  // Saving
  isSaving = false;
  submitError: string | null = null;

  private suggestTimer: ReturnType<typeof setTimeout> | undefined;

  constructor() {
    makeAutoObservable(this);
  }

  // ─── Open / Close ───

  openCreate = () => {
    this.reset();
    this.isOpen = true;
    this.isEditing = false;
  };

  openEdit = (contractor: SuperadminServiceCenterDetailDto) => {
    this.reset();
    this.isOpen = true;
    this.isEditing = true;
    this.editUuid = contractor.uuid;
    this.name = contractor.name;
    this.address = contractor.address;
    this.addressQuery = contractor.address;
    const loc = contractor.location as { lat?: number; lon?: number } | null;
    this.lat = loc?.lat ?? 55.7558;
    this.lon = loc?.lon ?? 37.6173;
    this.isPublished = Boolean(contractor.isPublished);
    this.loadExistingAdmins(contractor.uuid);
  };

  close = () => {
    this.isOpen = false;
  };

  reset = () => {
    clearTimeout(this.suggestTimer);
    this.suggestTimer = undefined;
    this.name = "";
    this.address = "";
    this.addressQuery = "";
    this.lat = 55.7558;
    this.lon = 37.6173;
    this.isPublished = false;
    this.admins = [];
    this.suggestions = [];
    this.showSuggestions = false;
    this.pendingReverseAddress = null;
    this.pendingSuggestedCoords = null;
    this.validationErrors = {};
    this.adminErrors = {};
    this.adminSubmitErrors = {};
    this.submitAttempted = false;
    this.isSaving = false;
    this.submitError = null;
    this.editUuid = null;
    this.isEditing = false;
  };

  // ─── Field setters ───

  setName = (v: string) => {
    this.name = v;
  };
  setAddress = (v: string) => {
    this.address = v;
  };
  setLat = (v: number) => {
    this.lat = v;
  };
  setLon = (v: number) => {
    this.lon = v;
  };
  setIsPublished = (v: boolean) => {
    this.isPublished = v;
  };

  setShowSuggestions = (v: boolean) => {
    this.showSuggestions = v;
  };

  setAddressQuery = (v: string) => {
    this.addressQuery = v;
    this.pendingReverseAddress = null; // user is actively editing — dismiss drag banner
    this.showSuggestions = true;
    clearTimeout(this.suggestTimer);
    if (v.trim().length >= 2) {
      this.suggestTimer = setTimeout(() => this.fetchSuggestions(v), 300);
    } else {
      this.suggestions = [];
    }
  };

  hideSuggestions = () => {
    this.showSuggestions = false;
  };

  selectSuggestion = (s: AddressSuggestion) => {
    this.address = s.label;
    this.addressQuery = s.label;
    this.lat = s.lat;
    this.lon = s.lon;
    this.showSuggestions = false;
  };

  /**
   * Called on address field blur.
   * If user typed but didn't click a suggestion — fetch and show a "Переставить точку" banner.
   * Does NOT auto-move the map, letting the user decide.
   */
  prepareSuggestionBanner = async () => {
    clearTimeout(this.suggestTimer);
    this.suggestTimer = undefined;
    this.showSuggestions = false;

    // Already in sync — nothing to suggest
    if (this.address === this.addressQuery) return;

    const query = this.addressQuery.trim();
    if (query.length < 2) return;

    await this.fetchSuggestions(query);

    if (this.suggestions.length > 0) {
      const first = this.suggestions[0];
      runInAction(() => {
        this.address = query; // persist what user typed as address
        this.pendingSuggestedCoords = first; // offer to move pin
        this.pendingReverseAddress = null;
      });
    } else {
      runInAction(() => {
        this.address = query;
      });
    }
  };

  /** Apply the pending reverse-geocoded address to the address input */
  applyReverseAddress = () => {
    if (!this.pendingReverseAddress) return;
    this.address = this.pendingReverseAddress;
    this.addressQuery = this.pendingReverseAddress;
    this.pendingReverseAddress = null;
  };

  dismissReverseAddress = () => {
    this.pendingReverseAddress = null;
  };

  /** Apply the pending suggestion coords — move the map pin to the found address */
  applyAddressToPoint = () => {
    if (!this.pendingSuggestedCoords) return;
    const s = this.pendingSuggestedCoords;
    this.lat = s.lat;
    this.lon = s.lon;
    this.address = s.label;
    this.addressQuery = s.label;
    this.pendingSuggestedCoords = null;
    this.pendingReverseAddress = null;
  };

  dismissAddressToPoint = () => {
    this.pendingSuggestedCoords = null;
  };

  setMapPosition = (lat: number, lon: number) => {
    this.lat = lat;
    this.lon = lon;
    this.pendingSuggestedCoords = null; // user moved pin — dismiss address banner
    this.reverseGeocode(this.lat, this.lon); // fetch address for the drag banner
  };

  /** Reverse geocoding via Nominatim (OSM) — free, no key needed */
  reverseGeocode = async (lat: number, lon: number) => {
    try {
      const url = `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lon}&zoom=18&addressdetails=1&accept-language=ru`;
      const res = await fetch(url, {
        headers: { "Accept-Language": "ru" },
      });
      if (!res.ok) return;
      const data = (await res.json()) as {
        address?: {
          state?: string;
          city?: string;
          town?: string;
          village?: string;
          suburb?: string;
          road?: string;
          pedestrian?: string;
          house_number?: string;
        };
      };

      const a = data.address;
      if (!a) return;

      const parts: string[] = [];
      if (a.state) parts.push(a.state);
      const locality = a.city ?? a.town ?? a.village;
      if (locality) parts.push(`г. ${locality}`);
      if (a.suburb && a.suburb !== locality) parts.push(a.suburb);
      const street = a.road ?? a.pedestrian;
      if (street) parts.push(`ул. ${street}`);
      if (a.house_number) parts.push(`д. ${a.house_number}`);

      if (parts.length === 0) return;
      const formatted = parts.join(", ");

      runInAction(() => {
        this.pendingReverseAddress = formatted; // offer to update — don't auto-apply
        this.suggestions = [];
        this.showSuggestions = false;
      });
    } catch {
      // Silently ignore — user can still edit manually
    }
  };

  // ─── Admins ───

  addAdmin = () => {
    this.admins.push({
      id: `new-${Date.now()}`,
      phone: "",
      phoneRegion: "7",
    });
  };

  updateAdminPhone = (id: string, value: string) => {
    const admin = this.admins.find((a) => a.id === id);
    if (admin) admin.phone = value;
  };

  updateAdminRegion = (id: string, region: string) => {
    const admin = this.admins.find((a) => a.id === id);
    if (admin) admin.phoneRegion = region; // allow empty — fallback to "7" only at API time
  };

  removeAdmin = (id: string) => {
    this.admins = this.admins.filter((a) => a.id !== id);
  };

  // ─── Computed ───

  get isValid(): boolean {
    return this.name.trim().length > 0 && this.address.trim().length > 0;
  }

  // ─── API: Dadata suggestions ───

  fetchSuggestions = async (query: string) => {
    this.isSuggestLoading = true;
    try {
      const result = await dadataControllerSuggestAddress({
        requestBody: { query },
      });
      runInAction(() => {
        const raw = result as DadataAddressResponse | null;
        const rawSuggestions = raw?.suggestions ?? [];
        this.suggestions = rawSuggestions
          .filter((s) => s.value && s.data.geo_lat && s.data.geo_lon)
          .map((s) => ({
            label: s.value,
            lat: Number(s.data.geo_lat),
            lon: Number(s.data.geo_lon),
          }));
        this.isSuggestLoading = false;
      });
    } catch {
      runInAction(() => {
        this.suggestions = [];
        this.isSuggestLoading = false;
      });
    }
  };

  // ─── API: Load existing admins (edit mode) ───

  private loadExistingAdmins = async (uuid: string) => {
    try {
      const result = await superadminListServiceCenterAdmins({
        serviceCenterUuid: uuid,
      });
      const rawItems = Array.isArray(result)
        ? (result as ServiceCenterAdminRaw[])
        : [];
      runInAction(() => {
        this.admins = rawItems.map((a) => ({
          id: a.uuid,
          phone: a.phone,
          isExisting: true,
        }));
      });
    } catch {
      toastStore.showError("Не удалось загрузить администраторов");
    }
  };

  // ─── Validation ───

  private validateForm = async (): Promise<boolean> => {
    const dto = Object.assign(new ServiceCenterFormDto(), {
      name: this.name,
      address: this.address,
    });
    const errors = await validate(dto);

    const errMap = errors.reduce<Record<string, string>>(
      (acc, err: ValidationError) => {
        if (err.constraints) {
          acc[err.property] = Object.values(err.constraints)[0];
        }
        return acc;
      },
      {},
    );

    // Validate new admins
    const adminErrMap: Record<string, Record<string, string>> = {};
    const newAdmins = this.admins.filter((a) => !a.isExisting);
    for (const admin of newAdmins) {
      const fullPhone = "+" + (admin.phoneRegion || "7") + admin.phone;
      const adDto = Object.assign(new AdminFormDto(), {
        phone: fullPhone,
      });
      const adErrors = await validate(adDto);
      if (adErrors.length > 0) {
        adminErrMap[admin.id] = adErrors.reduce<Record<string, string>>(
          (acc, err: ValidationError) => {
            if (err.constraints) {
              acc[err.property] = Object.values(err.constraints)[0];
            }
            return acc;
          },
          {},
        );
      }
    }

    runInAction(() => {
      this.validationErrors = errMap;
      this.adminErrors = adminErrMap;
    });

    return errors.length === 0 && Object.keys(adminErrMap).length === 0;
  };

  // ─── Submit ───

  submit = async (onSuccess?: () => void) => {
    this.submitAttempted = true;
    this.submitError = null;
    runInAction(() => {
      this.adminSubmitErrors = {};
    });
    const valid = await this.validateForm();
    if (!valid) return;

    this.isSaving = true;
    try {
      if (this.isEditing && this.editUuid) {
        await this.updateServiceCenter();
      } else {
        await this.createServiceCenter();
      }
      runInAction(() => {
        this.isSaving = false;
        if (Object.keys(this.adminSubmitErrors).length === 0) {
          this.isOpen = false;
          this.submitError = null;
        } else {
          this.submitError =
            "Сервисный центр сохранён, но часть администраторов не удалось добавить.";
        }
      });
      if (Object.keys(this.adminSubmitErrors).length === 0) {
        toastStore.showSuccess(
          this.isEditing
            ? "Сервисный центр обновлён"
            : "Сервисный центр создан",
        );
        onSuccess?.();
      }
    } catch (err) {
      runInAction(() => {
        this.isSaving = false;
        this.submitError = extractApiError(
          err,
          "Не удалось сохранить сервисный центр. Попробуйте ещё раз.",
        );
      });
    }
  };

  private createServiceCenter = async () => {
    const result = await superadminCreateServiceCenter({
      requestBody: {
        name: this.name,
        address: this.address,
        location: { lat: this.lat, lon: this.lon },
        working_hours: {},
        contacts: {},
      },
    });

    const uuid = (result as CreateServiceCenterResult | null)?.uuid;
    if (!uuid) return;

    // Update isPublished if needed
    if (this.isPublished) {
      await superadminUpdateServiceCenter({
        uuid,
        requestBody: { isPublished: true },
      });
    }

    // Create admins — collect per-admin errors instead of throwing
    const newAdmins = this.admins.filter((a) => !a.isExisting);
    if (newAdmins.length > 0) {
      const results = await Promise.allSettled(
        newAdmins.map((a) =>
          superadminCreateServiceCenterAdmin({
            serviceCenterUuid: uuid,
            requestBody: buildAdminRequestBody(
              "+" + (a.phoneRegion || "7") + a.phone,
            ),
          }),
        ),
      );
      const errMap: Record<string, string> = {};
      results.forEach((r, i) => {
        if (r.status === "rejected") {
          errMap[newAdmins[i].id] = extractApiError(
            r.reason,
            "Не удалось создать администратора.",
          );
        }
      });
      if (Object.keys(errMap).length > 0) {
        runInAction(() => {
          this.adminSubmitErrors = errMap;
        });
      }
    }
  };

  private updateServiceCenter = async () => {
    if (!this.editUuid) return;

    await superadminUpdateServiceCenter({
      uuid: this.editUuid,
      requestBody: {
        name: this.name,
        address: this.address,
        location: { lat: this.lat, lon: this.lon },
        isPublished: this.isPublished,
      },
    });

    // Create new admins — collect per-admin errors
    const newAdmins = this.admins.filter((a) => !a.isExisting);
    if (newAdmins.length > 0) {
      const results = await Promise.allSettled(
        newAdmins.map((a) =>
          superadminCreateServiceCenterAdmin({
            serviceCenterUuid: this.editUuid!,
            requestBody: buildAdminRequestBody(
              "+" + (a.phoneRegion || "7") + a.phone,
            ),
          }),
        ),
      );
      const errMap: Record<string, string> = {};
      results.forEach((r, i) => {
        if (r.status === "rejected") {
          errMap[newAdmins[i].id] = extractApiError(
            r.reason,
            "Не удалось создать администратора.",
          );
        }
      });
      if (Object.keys(errMap).length > 0) {
        runInAction(() => {
          this.adminSubmitErrors = errMap;
        });
      }
    }
  };
}

export const serviceCenterFormStore = new ServiceCenterFormStore();
export type { ServiceCenterFormStore };
